import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeSlash } from '@phosphor-icons/react';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            navigate('/');
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image (foto Main Solution) */}
            <div 
                className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
                style={{ 
                    backgroundImage: 'url(/login_bg.png)' 
                }}
            >
                <div className="absolute inset-0" style={{background: 'linear-gradient(135deg, rgba(11,42,74,0.78) 0%, rgba(0,124,180,0.72) 100%)'}} />
                <div className="relative z-10 flex flex-col justify-center p-12 text-white">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-lg">
                            <img src="/mainsolution_logo.jpg" alt="Main Solution" className="w-full h-full object-contain p-1" />
                        </div>
                        <div className="flex flex-col leading-tight">
                            <span className="font-heading text-2xl font-bold">Main Solution</span>
                            <span className="text-[11px] uppercase tracking-[2px] text-cyan-200 font-semibold">CRM · Energie Rinnovabili</span>
                        </div>
                    </div>
                    <h1 className="font-heading text-4xl font-bold mb-4">
                        Gestione Commesse Fotovoltaiche
                    </h1>
                    <p className="text-lg text-white/85 max-w-md">
                        Piattaforma integrata per la gestione end-to-end delle commesse, 
                        dal contratto alla chiusura GSE.
                    </p>
                </div>
            </div>

            {/* Right side - Login form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-background">
                <Card className="w-full max-w-md border-border shadow-lg">
                    <CardHeader className="text-center pb-2">
                        <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                            <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center overflow-hidden ring-1 ring-slate-200">
                                <img src="/mainsolution_logo.jpg" alt="Main Solution" className="w-full h-full object-contain p-1" />
                            </div>
                            <span className="font-heading text-xl font-bold">Main Solution</span>
                        </div>
                        <CardTitle className="font-heading text-2xl">Accedi</CardTitle>
                        <CardDescription>
                            Inserisci le tue credenziali per accedere al sistema
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div 
                                    data-testid="login-error"
                                    className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
                                >
                                    {error}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    data-testid="login-email-input"
                                    placeholder="nome@esempio.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        data-testid="login-password-input"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete="current-password"
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                data-testid="login-submit-button"
                                className="w-full"
                                disabled={loading}
                            >
                                {loading ? 'Accesso in corso...' : 'Accedi'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
